/**
 * 纠错详情弹窗组件
 * 
 * 功能:
 * 1. 展示纠错记录的完整信息（纠错对象、错误描述、建议修正、佐证图片等）
 * 2. 展示题目详细信息（题干、选项、参考答案、解析、评分规则）- 与 QuizReviewMode 完全一致
 * 3. 支持管理员模式（显示审核操作）和学员模式（仅查看）
 * 4. 支持图片预览
 * 5. 已审核/拒绝状态下按钮置灰不可点击
 */

import React, { useState } from 'react';
import { X, Eye, Image as ImageIcon, Clock, CheckCircle, User, FileText, AlertCircle } from 'lucide-react';
import { extractBeijingTime } from '../utils/timeUtils';
import QuestionDetailDisplay from './QuestionDetailDisplay';

interface Correction {
  id: string;
  submitter_id: string;
  submitter_username?: string;
  target_type: string;
  target_id: string;
  error_description: string;
  suggested_correction: string | null;
  evidence_images: string[] | null;
  severity: string | null;
  status: string;
  reviewer_id: string | null;
  review_comment: string | null;
  reviewed_at: string | null;
  reward_points: number | null;
  created_at: string;
  
  // 扩展字段
  paper_title?: string | null;
  question_title?: string | null;
  question_type?: string | null;
  question_order_index?: number | null;
  question_content?: string | null;
  question_options?: any | null;
  reference_answer?: string | null;
  explanation?: string | null;
  scoring_points?: any | null;
  title_images?: string[] | null;
  title_image_widths?: number[] | null;
  title_image_layout?: string | null;
  image_position?: string | null;
  option_layout?: string | null;
}

interface CorrectionDetailModalProps {
  correction: Correction;
  isOpen: boolean;
  onClose: () => void;
  
  // 管理员模式相关
  isAdminMode?: boolean;
  onApprove?: (rewardPoints: number, comment: string) => Promise<void>;
  onReject?: (comment: string) => Promise<void>;
  defaultRewardPoints?: number;
}

const TARGET_TYPE_LABELS: Record<string, string> = {
  question: '题目纠错',
  paper: '试卷纠错',
  answer: '答案纠错',
  option: '选项纠错',
  explanation: '解析纠错',
  other: '其他问题',
};

const STATUS_CONFIG: Record<string, { label: string; color: string }> = {
  pending: { label: '待审核', color: 'bg-yellow-100 text-yellow-700' },
  reviewing: { label: '审核中', color: 'bg-blue-100 text-blue-700' },
  approved: { label: '已通过', color: 'bg-green-100 text-green-700' },
  rejected: { label: '已拒绝', color: 'bg-red-100 text-red-700' },
  rewarded: { label: '已奖励', color: 'bg-emerald-100 text-emerald-700' },
  processed: { label: '已处理', color: 'bg-gray-100 text-gray-700' },
};

const QUESTION_TYPE_MAP: Record<string, string> = {
  single_choice: '单选题',
  multi_choice: '多选题',
  multiple_choice: '多选题',
  judge: '判断题',
  subjective: '主观题',
  coding: '编程题',
  python: 'Python编程',
  python_coding: 'Python编程',
  cpp: 'C++编程',
  cpp_coding: 'C++编程',
  graphic: '图形化编程',
  graphics: '图形化编程',
  scratch_coding: '图形化编程',
};

const getQuestionTypeLabel = (questionType: string | null | undefined): string => {
  return QUESTION_TYPE_MAP[questionType || ''] || '题目';
};

const formatCorrectionTarget = (correction: Correction): string => {
  const paperName = correction.paper_title || '未命名试卷';

  if (correction.target_type === 'question') {
    const questionType = correction.question_type;
    const questionTitle = correction.question_title || '';
    const orderIndex = correction.question_order_index;
    const typeLabel = getQuestionTypeLabel(questionType);

    const parts = [paperName, typeLabel];
    if (orderIndex != null) {
      parts.push(`第${orderIndex}题`);
    }
    if (questionTitle) {
      parts.push(questionTitle);
    }

    return `【${parts.join(' ')}】`;
  }

  return `${TARGET_TYPE_LABELS[correction.target_type] || '题目'}：【${paperName}】`;
};

const formatDate = (dateStr: string) => {
  const bj = extractBeijingTime(dateStr);
  if (!bj) return '-';
  return `${bj.year}-${bj.month}-${bj.day} ${bj.hour}:${bj.minute}`;
};

export default function CorrectionDetailModal({
  correction,
  isOpen,
  onClose,
  isAdminMode = false,
  onApprove,
  onReject,
  defaultRewardPoints = 50,
}: CorrectionDetailModalProps) {
  const [previewImage, setPreviewImage] = useState<string | null>(null);
  const [rewardPoints, setRewardPoints] = useState(defaultRewardPoints);
  const [reviewComment, setReviewComment] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // 判断是否已审核（通过或拒绝），此时按钮应置灰
  const isReviewed = correction.status === 'approved' || correction.status === 'rejected' || correction.status === 'processed';

  // 重置表单状态
  React.useEffect(() => {
    if (isOpen) {
      setRewardPoints(defaultRewardPoints);
      setReviewComment('');
      setPreviewImage(null);
    }
  }, [isOpen, defaultRewardPoints]);

  const handleApprove = async () => {
    if (!onApprove || isReviewed) return;
    setSubmitting(true);
    try {
      await onApprove(rewardPoints, reviewComment);
      onClose();
    } catch (error) {
      console.error('Failed to approve:', error);
      alert('审核失败，请重试');
    } finally {
      setSubmitting(false);
    }
  };

  const handleReject = async () => {
    if (!onReject || isReviewed) return;
    setSubmitting(true);
    try {
      await onReject(reviewComment);
      onClose();
    } catch (error) {
      console.error('Failed to reject:', error);
      alert('操作失败，请重试');
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  const statusInfo = STATUS_CONFIG[correction.status] || { label: correction.status, color: 'bg-gray-100 text-gray-700' };
  const hasQuestionData = correction.target_type === 'question' && (correction.question_content || correction.question_options);

  // 获取选项布局
  const optionLayout = (correction as any).option_layout || 'grid';

  return (
    <>
      {/* 主弹窗 */}
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
        <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
          {/* 弹窗头部 */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 flex-shrink-0">
            <h2 className="text-lg font-bold text-gray-800">纠错详情</h2>
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 rounded-full transition-colors"
            >
              <X className="w-5 h-5 text-gray-500" />
            </button>
          </div>

          {/* 弹窗内容 - 可滚动 */}
          <div className="flex-1 overflow-y-auto p-6 space-y-6">
            {/* Section 1: 纠错对象信息 */}
            <div>
              <div className="text-xs text-gray-500 mb-2 flex items-center gap-1">
                <FileText className="w-3.5 h-3.5" />
                纠错对象
              </div>
              <div className="text-sm font-medium text-gray-800 bg-blue-50 rounded-lg px-4 py-3 border border-blue-100">
                {formatCorrectionTarget(correction)}
              </div>
            </div>

            {/* Section 2: 纠错记录信息 */}
            <div className="space-y-4">
              <div className="text-xs text-gray-500 mb-2 flex items-center gap-1">
                <AlertCircle className="w-3.5 h-3.5" />
                纠错记录信息
              </div>

              {/* 提交人 + 状态 */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <div className="text-xs text-gray-500 mb-1">提交人</div>
                  <div className="text-sm text-gray-800 flex items-center gap-1">
                    <User className="w-3.5 h-3.5" />
                    {correction.submitter_username || '未知用户'}
                  </div>
                </div>
                <div>
                  <div className="text-xs text-gray-500 mb-1">当前状态</div>
                  <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${statusInfo.color}`}>
                    {statusInfo.label}
                  </span>
                </div>
              </div>

              {/* 错误描述 */}
              <div>
                <div className="text-xs text-gray-500 mb-1">错误描述</div>
                <div className="text-sm text-gray-800 bg-gray-50 rounded-lg px-4 py-3 border border-gray-100 whitespace-pre-wrap">
                  {correction.error_description}
                </div>
              </div>

              {/* 建议修正 */}
              {correction.suggested_correction && (
                <div>
                  <div className="text-xs text-gray-500 mb-1">建议修正</div>
                  <div className="text-sm text-gray-800 bg-green-50 rounded-lg px-4 py-3 border border-green-100 whitespace-pre-wrap">
                    {correction.suggested_correction}
                  </div>
                </div>
              )}

              {/* 佐证图片 */}
              {correction.evidence_images && correction.evidence_images.length > 0 && (
                <div>
                  <div className="text-xs text-gray-500 mb-2 flex items-center gap-1">
                    <ImageIcon className="w-3.5 h-3.5" />
                    佐证图片 ({correction.evidence_images.length})
                  </div>
                  <div className="grid grid-cols-3 gap-3">
                    {correction.evidence_images.map((imgUrl, idx) => (
                      <div
                        key={idx}
                        className="relative aspect-square rounded-lg overflow-hidden border border-gray-200 cursor-pointer hover:border-blue-400 transition-colors group"
                        onClick={() => setPreviewImage(imgUrl)}
                      >
                        <img
                          src={imgUrl}
                          alt={`佐证图片 ${idx + 1}`}
                          className="w-full h-full object-cover"
                        />
                        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors flex items-center justify-center">
                          <Eye className="w-6 h-6 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* 审核意见 */}
              {correction.review_comment && (
                <div>
                  <div className="text-xs text-gray-500 mb-1">审核意见</div>
                  <div className={`text-sm rounded-lg px-4 py-3 border whitespace-pre-wrap ${
                    correction.status === 'rejected'
                      ? 'bg-red-50 text-red-800 border-red-100'
                      : 'bg-blue-50 text-blue-800 border-blue-100'
                  }`}>
                    {correction.review_comment}
                  </div>
                </div>
              )}

              {/* 奖励U币 */}
              {correction.reward_points && correction.reward_points > 0 && (
                <div>
                  <div className="text-xs text-gray-500 mb-1">奖励U币</div>
                  <div className="text-xl font-bold text-orange-600">+{correction.reward_points}</div>
                </div>
              )}

              {/* 时间信息 */}
              <div className="pt-4 border-t border-gray-100">
                <div className="flex items-center justify-between text-xs text-gray-500">
                  <div className="flex items-center gap-1">
                    <Clock className="w-3 h-3" />
                    <span>提交时间</span>
                    <span className="ml-1">{formatDate(correction.created_at)}</span>
                  </div>
                  {correction.reviewed_at && (
                    <div className="flex items-center gap-1">
                      <CheckCircle className="w-3 h-3" />
                      <span>审核时间</span>
                      <span className="ml-1">{formatDate(correction.reviewed_at)}</span>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Section 3: 题目详细信息（仅当有题目数据时显示）- 使用公共组件 QuestionDetailDisplay */}
            {hasQuestionData && (
              <div className="pt-6 border-t border-gray-200">
                <div className="text-xs text-gray-500 mb-3 flex items-center gap-1">
                  <FileText className="w-3.5 h-3.5" />
                  题目详细信息
                </div>

                <QuestionDetailDisplay
                  orderIndex={correction.question_order_index}
                  questionType={correction.question_type}
                  questionTitle={correction.question_title}
                  questionContent={correction.question_content}
                  questionOptions={correction.question_options}
                  titleImages={correction.title_images}
                  titleImageWidths={correction.title_image_widths}
                  titleImageLayout={correction.title_image_layout}
                  imagePosition={correction.image_position}
                  optionLayout={optionLayout}
                  referenceAnswer={correction.reference_answer}
                  explanation={correction.explanation}
                  scoringPoints={correction.scoring_points}
                  showOptions={true}
                  showAnswer={true}
                  showExplanation={true}
                  showScoringRules={!['single_choice', 'multi_choice', 'multiple_choice', 'judge'].includes(correction.question_type || '')}
                />
              </div>
            )}
          </div>

          {/* 弹窗底部 - 固定 */}
          {isAdminMode && onApprove && onReject ? (
            <div className="sticky bottom-0 bg-white border-t border-gray-200 p-4 space-y-3 flex-shrink-0">
              {isReviewed ? (
                /* 已审核状态：显示只读信息和关闭按钮 */
                <div className="space-y-3">
                  <div className="flex items-center gap-2 text-sm text-gray-500">
                    <CheckCircle className="w-4 h-4" />
                    <span>该纠错记录已{correction.status === 'approved' ? '通过' : correction.status === 'rejected' ? '拒绝' : '处理'}，不可重复操作</span>
                  </div>
                  <button
                    onClick={onClose}
                    className="w-full py-2.5 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors font-medium"
                  >
                    关闭
                  </button>
                </div>
              ) : (
                /* 待审核状态：显示审核表单 */
                <>
                  {/* 奖励U币输入 */}
                  <div className="flex items-center gap-3">
                    <label className="text-sm font-medium text-gray-700 whitespace-nowrap">奖励U币:</label>
                    <input
                      type="number"
                      value={rewardPoints}
                      onChange={(e) => setRewardPoints(parseInt(e.target.value) || 0)}
                      min="0"
                      disabled={submitting}
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none disabled:bg-gray-100 disabled:cursor-not-allowed"
                    />
                  </div>

                  {/* 审核意见输入 */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">审核意见（将发送给学员）</label>
                    <textarea
                      value={reviewComment}
                      onChange={(e) => setReviewComment(e.target.value)}
                      placeholder="请输入审核意见..."
                      rows={2}
                      disabled={submitting}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg resize-none focus:ring-2 focus:ring-blue-500 outline-none disabled:bg-gray-100 disabled:cursor-not-allowed"
                    />
                  </div>

                  {/* 操作按钮 */}
                  <div className="flex gap-3">
                    <button
                      onClick={handleApprove}
                      disabled={submitting}
                      className="flex-1 py-2.5 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {submitting ? '处理中...' : '通过并发放奖励'}
                    </button>
                    <button
                      onClick={handleReject}
                      disabled={submitting}
                      className="flex-1 py-2.5 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {submitting ? '处理中...' : '拒绝'}
                    </button>
                    <button
                      onClick={onClose}
                      disabled={submitting}
                      className="flex-1 py-2.5 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      取消
                    </button>
                  </div>
                </>
              )}
            </div>
          ) : (
            <div className="sticky bottom-0 bg-white border-t border-gray-200 p-4 flex-shrink-0">
              <button
                onClick={onClose}
                className="w-full py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
              >
                关闭
              </button>
            </div>
          )}
        </div>
      </div>

      {/* 图片预览弹窗 */}
      {previewImage && (
        <div
          className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/90 backdrop-blur-sm"
          onClick={() => setPreviewImage(null)}
        >
          <button
            onClick={() => setPreviewImage(null)}
            className="absolute top-4 right-4 p-2 bg-white/10 hover:bg-white/20 rounded-full text-white transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
          <img
            src={previewImage}
            alt="预览"
            className="max-w-full max-h-full object-contain rounded-lg shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </>
  );
}
